/**
 * Stayline brand geometry — the single source for the wordmark and the
 * monogram tile, shared by `<Logo>`, `<BrandLoader>` and
 * `scripts/brand-assets.ts` (favicon / app icon / OG cover).
 *
 * Outlines are Pretendard Bold (SIL OFL 1.1) set in font units (2048/em) at
 * −0.04em tracking, baseline at y=0. The wordmark uses a dotless ı so the
 * i's dot can be drawn as its own circle in the accent colour — "the head of
 * the line, right now" from the landing's 한 줄의 체류 concept. Outlines
 * rather than live text keep the lockup identical in the favicon, the OG
 * card and the app, whatever fonts are loaded.
 */

/** The single accent: the guest chat bubble colour, also the landing's line head. */
export const BRAND_ACCENT = "#B4501A";
/** Brand ink and paper (landing palette `ink` / `ground`). */
export const BRAND_INK = "#0A0A0A";
export const BRAND_PAPER = "#FFFFFF";

export interface Dot {
  cx: number;
  cy: number;
  r: number;
}

/** `stayline` in lowercase with the i's dot lifted out. */
export const WORDMARK = {
  viewBox: { x: 66, y: -1555, width: 6899, height: 1963 },
  path: "M736 -764H1014C998 -970 828 -1100 554 -1100C274 -1100 94 -970 96 -762C94 -602 202 -494 408 -454L588 -418C690 -396 736 -362 736 -306C736 -236 664 -192 556 -192C448 -192 378 -236 364 -322H66C90 -110 262 22 556 22C844 22 1036 -122 1038 -336C1036 -492 934 -586 724 -630L530 -670C428 -692 388 -730 390 -778C388 -844 462 -890 558 -890C656 -890 724 -840 736 -764Z M1710.1 -1086H1506.1V-1346H1210.1V-1086H1060.1V-864H1210.1V-292C1208.1 -82 1354.1 14 1568.1 14C1642.1 14 1694.1 10 1726.1 4V-230C1690.1 -226 1638.1 -224 1608.1 -224C1548.1 -226 1506.1 -248 1506.1 -334V-864H1710.1Z M1762.2 -304C1762.2 -88 1916.2 20 2124.2 20C2286.2 20 2388.2 -50 2442.2 -150H2450.2V0H2730.2V-732C2730.2 -994 2506.2 -1100 2270.2 -1100C1982.2 -1100 1818.2 -952 1800.2 -764H2082.2C2098.2 -838 2162.2 -884 2262.2 -884C2372.2 -884 2434.2 -830 2436.2 -740V-652C2390.2 -650 2244.2 -643 2166.2 -638C1958.2 -626 1762.2 -546 1762.2 -304ZM2046.2 -312C2046.2 -396 2112.2 -444 2214.2 -454C2265.2 -459 2391.2 -466 2436.2 -468V-388C2438.2 -274 2342.2 -184 2208.2 -184C2114.2 -184 2046.2 -228 2046.2 -312Z M3048.2 408C3278.2 408 3396.2 294 3458.2 120L3882.2 -1086H3568.2L3338.2 -286H3326.2L3098.2 -1086H2788.2L3178.2 30L3160.2 76C3124.2 170 3076.2 176 3018.2 176C2980.2 176 2936.2 174 2898.2 172V402C2936.2 406 2990.2 408 3048.2 408Z M4240.3 -1448H3944.3V0H4240.3Z M4388.4 0H4684.4V-1086H4388.4Z M5128.5 -628C5128.5 -772 5212.5 -854 5336.5 -854C5458.5 -854 5532.5 -774 5532.5 -640V0H5826.5V-692C5828.5 -946 5678.5 -1100 5452.5 -1100C5292.5 -1100 5176.5 -1022 5126.5 -896H5114.5V-1086H4832.5V0H5128.5Z M6466.6 22C6734.6 22 6914.6 -110 6956.6 -310H6674.6C6644.6 -244 6572.6 -200 6472.6 -200C6320.6 -200 6222.6 -302 6222.6 -466H6964.6V-550C6964.6 -920 6740.6 -1100 6454.6 -1100C6136.6 -1100 5930.6 -874 5930.6 -538C5930.6 -194 6134.6 22 6466.6 22ZM6222.6 -652C6227.6 -777 6324.6 -878 6460.6 -878C6592.6 -878 6684.6 -784 6684.6 -652Z",
  /** Centred over the ı stem, at the height of Pretendard's own i dot, ~9% larger. */
  dot: { cx: 4536.4, cy: -1379, r: 176 } satisfies Dot,
} as const;

/** Width ÷ height of the wordmark box, for sizing it from a height. */
export const WORDMARK_ASPECT = WORDMARK.viewBox.width / WORDMARK.viewBox.height;

/** The app-icon tile: a lowercase `s` followed by the accent full stop. */
export const MONOGRAM = {
  size: 1024,
  /** Corner radius of the tile (≈ 22% — iOS-like squircle approximation). */
  radius: 228,
  /** Places the glyph group (font units) in the 1024 tile. */
  transform: "translate(183 738) scale(0.42)",
  s: WORDMARK.path.slice(0, WORDMARK.path.indexOf("Z") + 1),
  dot: { cx: 1324, cy: -176, r: 176 } satisfies Dot,
} as const;

export function wordmarkViewBox(): string {
  const { x, y, width, height } = WORDMARK.viewBox;
  return `${x} ${y} ${width} ${height}`;
}

interface MonogramSvgOptions {
  /** Draw the rounded tile; false = full-bleed square (iOS rounds it itself). */
  rounded?: boolean;
  tile?: string;
  glyph?: string;
}

/** Standalone monogram SVG markup (favicon, raster sources). */
export function monogramSvg({
  rounded = true,
  tile = BRAND_INK,
  glyph = BRAND_PAPER,
}: MonogramSvgOptions = {}): string {
  const { size, radius, transform, s, dot } = MONOGRAM;
  const rx = rounded ? radius : 0;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">`,
    `<rect width="${size}" height="${size}" rx="${rx}" fill="${tile}"/>`,
    `<g transform="${transform}">`,
    `<path d="${s}" fill="${glyph}"/>`,
    `<circle cx="${dot.cx}" cy="${dot.cy}" r="${dot.r}" fill="${BRAND_ACCENT}"/>`,
    "</g>",
    "</svg>",
  ].join("");
}
